import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarPlus, Copy, Loader2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import { calendarFeed, calendarFeedCreate, calendarFeedDelete } from "../lib/api";
import { tr } from "../lib/i18n";

const copyText = (s) => { navigator.clipboard?.writeText(s); toast.success(tr("Copied")); };

// Links + actions for one calendar feed (used by the member's own row and by the owner's per-member list).
export const FeedLinks = ({ data, busy, onRotate, onDisable, onCreate, testid }) => {
  const [show, setShow] = useState(false);
  if (!data?.enabled) {
    return <Button size="sm" className="rounded-full gap-1.5" onClick={onCreate} disabled={busy} data-testid={`${testid}-create`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="h-3.5 w-3.5" />} {tr("Create calendar link")}</Button>;
  }
  return (
    <div className="space-y-2" data-testid={`${testid}-links`}>
      <div className="flex items-center gap-2">
        <code className="block flex-1 truncate rounded-md bg-background border border-border px-2.5 py-1.5 text-[11px]" data-testid={`${testid}-url`}>{show ? data.ics : data.ics.replace(/calendar\/.*\.ics$/, "calendar/••••••••.ics")}</code>
        <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setShow((v) => !v)} title={show ? "Hide" : "Show"} data-testid={`${testid}-reveal`}>{show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" className="h-7 rounded-full gap-1 text-xs" asChild><a href={data.google} target="_blank" rel="noreferrer" data-testid={`${testid}-google`}><ExternalLink className="h-3 w-3" /> Google</a></Button>
        <Button size="sm" variant="outline" className="h-7 rounded-full gap-1 text-xs" asChild><a href={data.webcal} data-testid={`${testid}-apple`}><CalendarPlus className="h-3 w-3" /> Apple / Outlook</a></Button>
        <Button size="sm" variant="outline" className="h-7 rounded-full gap-1 text-xs" onClick={() => copyText(data.ics)} data-testid={`${testid}-copy`}><Copy className="h-3 w-3" /> {tr("Copy link")}</Button>
        <span className="ml-auto flex gap-3 text-xs text-muted-foreground items-center">
          <button type="button" className="hover:text-foreground" onClick={onRotate} disabled={busy} data-testid={`${testid}-rotate`}>{tr("Rotate")}</button>
          <button type="button" className="hover:text-destructive" onClick={onDisable} disabled={busy} data-testid={`${testid}-disable`}>{tr("Disable")}</button>
        </span>
      </div>
      <div className="text-[11px] text-muted-foreground">{data.last_fetched_at ? `${tr("Last read by a calendar app")} ${new Date(data.last_fetched_at).toLocaleString()}` : tr("Not read by a calendar app yet")}</div>
    </div>
  );
};

// The signed-in member's own private subscription — filtered to what their role may see.
export const CalendarFeedRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["calendar-feed"], queryFn: calendarFeed });
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { qc.setQueryData(["calendar-feed"], await fn()); qc.invalidateQueries({ queryKey: ["calendar-members"] }); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || tr("Something went wrong")); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="calendar-row">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><CalendarPlus className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm flex items-center gap-2 flex-wrap">{tr("My calendar subscription")}{data?.scope && <span className="text-[10px] uppercase tracking-wider font-mono rounded-full px-2 py-0.5 bg-primary/10 text-primary" data-testid="calendar-scope">{tr(data.scope.label)}</span>}</div>
          <p className="text-xs text-muted-foreground mt-0.5">{data?.scope ? tr(data.scope.detail) : tr("Dates from the vault in Google Calendar, Apple Calendar or Outlook.")} {tr("The link is private to you — it shows only what your role may see.")}</p>
          <div className="mt-3">
            <FeedLinks data={data} busy={busy} testid="calendar-mine" onCreate={() => run(calendarFeedCreate, tr("Calendar link created"))} onRotate={() => run(calendarFeedCreate, tr("New link created — the old one stopped working"))} onDisable={() => run(calendarFeedDelete, tr("Calendar link disabled"))} />
          </div>
        </div>
      </div>
    </div>
  );
};
